import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";

// Import PageFormPreview and LocalSessionProvider for the isolated route
import AdminBarHeadsup from "~/components/AdminBarHeadsup";
// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================
import LayoutRoot from "~/LayoutRoot";
import PageFormPreview from "~/pages/providerWrapped/forms/[templateSlug]/[stateSlug]/preview/PageFormPreview";
import LayoutProviderWrapped from "~/pages/providerWrapped/LayoutProviderWrapped";
import LayoutHasSidebar from "~/pages/providerWrapped/private/hasSidebar/LayoutHasSidebar";
import LayoutPrivate from "~/pages/providerWrapped/private/LayoutPrivate";
// ============================================================================
// PAGE COMPONENTS
// ============================================================================
import RouteErrorBoundary from "~/pages/RouteErrorBoundary";
import LocalSessionProvider from "~/providers/LocalSessionProvider";
// ============================================================================
// ROUTE MODULES
// ============================================================================
import { authRoutes, betaAuthRoutes } from "~/routes/auth.routes";
import { formsRoutes } from "~/routes/forms.routes";
import { invoiceRoutes } from "~/routes/invoices.routes";
import { landlordRoutes } from "~/routes/landlord.routes";
import {
  notificationsRoutes,
  settingsRoutes,
} from "~/routes/settings-notifications.routes";
import { tenantRoutes } from "~/routes/tenant.routes";

// ============================================================================
// LAZY LOADED COMPONENTS
// ============================================================================
const PagePrivate = lazy(
  () => import("~/pages/providerWrapped/private/PagePrivate")
);
const PageEmailNotVerified = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/PageEmailNotVerified")
);

// ============================================================================
// ROUTER CONFIGURATION
// ============================================================================

export const router = createBrowserRouter(
  [
    {
      Component: LayoutRoot,
      errorElement: <RouteErrorBoundary />,
      children: [
        // ================================================================
        // FORM PREVIEW ROUTE - Outside providers to prevent rerenders
        // ================================================================
        {
          path: "forms/:templateSlug/:stateSlug/preview",
          element: (
            <LocalSessionProvider>
              <AdminBarHeadsup />
              <Suspense fallback={null}>
                <PageFormPreview />
              </Suspense>
            </LocalSessionProvider>
          ),
        },

        {
          Component: LayoutProviderWrapped,
          children: [
            // ================================================================
            // AUTHENTICATION ROUTES
            // ================================================================
            authRoutes,
            betaAuthRoutes,
            // ================================================================
            // FORM ROUTES (excluding preview)
            // ================================================================
            formsRoutes,
            // ================================================================
            // PRIVATE AUTHENTICATED ROUTES
            // ================================================================
            {
              Component: LayoutPrivate,
              children: [
                // Root Private Page
                {
                  path: "/",
                  element: (
                    <Suspense fallback={null}>
                      <PagePrivate />
                    </Suspense>
                  ),
                },

                // ============================================================
                // NO SIDEBAR ROUTES (Full-width pages)
                // ============================================================
                ...invoiceRoutes,

                // ============================================================
                // HAS SIDEBAR ROUTES (Standard app pages with navigation)
                // ============================================================
                {
                  Component: LayoutHasSidebar,
                  children: [
                    // General Pages
                    {
                      path: "email-not-verified",
                      element: (
                        <Suspense fallback={null}>
                          <PageEmailNotVerified />
                        </Suspense>
                      ),
                    },

                    // ========================================================
                    // LANDLORD ROUTES
                    // ========================================================
                    landlordRoutes,

                    // ========================================================
                    // NOTIFICATIONS & SETTINGS ROUTES
                    // ========================================================
                    notificationsRoutes,
                    ...settingsRoutes,

                    // ========================================================
                    // TENANT ROUTES
                    // ========================================================
                    tenantRoutes,
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    basename: "/app",
  }
);
